import React from "react";
import Modal from "./Modal";
import StatusPill from "./StatusPill";
import AnticipoIndicador from "./AnticipoIndicador";
import { Receipt } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

function Comprobante({ cita }) {
  if (!cita?.comprobante_recibido_at) return null;
  async function descargar(e) {
    e.preventDefault();
    try {
      const r = await api.get(`/files/${encodeURIComponent(cita.comprobante_document_id)}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      const m = /filename\*=UTF-8''([^;]+)/.exec(r.headers?.["content-disposition"] || "");
      a.download = m ? decodeURIComponent(m[1]) : "comprobante";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("No se pudo descargar el documento"); }
  }
  return cita.comprobante_document_id ? (
    <a href="#comprobante" onClick={descargar} className="text-sm text-brand-electric underline" data-testid={`comprobante-${cita.id}`}>Comprobante recibido</a>
  ) : <span className="text-sm" data-testid={`comprobante-${cita.id}`}>Comprobante recibido</span>;
}

function timeToMinutes(hhmm) {
  const [h, m] = String(hhmm).split(":").map(Number);
  return h * 60 + (m || 0);
}
function minutesToHHMM(mins) {
  const wrapped = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function apptRange(a) {
  const start = timeToMinutes(a.time);
  const dur = a.duration_min ?? 30;
  return [start, start + dur];
}
function rangesIntersect(a, b) {
  const [s1, e1] = apptRange(a);
  const [s2, e2] = apptRange(b);
  return s1 < e2 && s2 < e1;
}

// Panel de ocupacion (Bloque 2C): dado el id de la cita en la que se hizo clic, arma en el
// cliente -- sin pedir nada nuevo al backend, ya tenemos `appointments` cargado -- la lista de
// TODAS las citas de ese dia cuyo rango [time, time+duration_min) se intersecta con el de la
// seleccionada. Es solape de RANGO (una cita de 60 min a las 10:00 se relaciona con una de
// las 10:30), no coincidencia de hora exacta.
// `onCobrar` llega sin definir en sesion demo: el boton no se pinta (no se deja fallar
// con el 403 generico -- DT.71).
// `onAnticipo` llega sin definir en demo y sin la capacidad `cobros`: el indicador del
// anticipo se pinta igual, pero como marca y no como boton (CL.4 A2).
export default function OccupancyPanel({ open, onClose, appointments, selectedId, onCobrar, onAnticipo }) {
  const selected = appointments.find((a) => a.id === selectedId);

  const related = selected
    ? [
        selected,
        ...appointments.filter(
          (a) =>
            a.id !== selected.id &&
            a.date === selected.date &&
            a.status !== "Cancelado" &&
            rangesIntersect(a, selected)
        ),
      ].sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time))
    : [];

  // Mismo prestador en dos citas relacionadas = empalme real (el trigger lo bloquearia salvo
  // allow_overlap). Distinto prestador a la misma hora es operacion normal del dia a dia.
  const doctorCounts = {};
  related.forEach((a) => {
    if (a.doctor_id) doctorCounts[a.doctor_id] = (doctorCounts[a.doctor_id] || 0) + 1;
  });

  return (
    <Modal open={open && !!selected} onClose={onClose} title="Ocupación de horario" size="md" testid="modal-occupancy">
      <Comprobante cita={selected} />
      {selected?.solicitante_nombre && (
        <p className="text-sm text-slate-700 mb-3" data-testid="cita-solicitante">
          Pidió la cita: {selected.solicitante_nombre} ({selected.solicitante_parentesco})
        </p>
      )}
      {!selected ? null : related.length === 1 ? (
        <p className="text-sm text-slate-500" data-testid="occupancy-empty">
          No hay otras citas en este horario.
        </p>
      ) : (
        <ul className="space-y-2">
          {related.map((a) => {
            const [s, e] = apptRange(a);
            const isSelected = a.id === selected.id;
            const sameProviderConflict = a.doctor_id && doctorCounts[a.doctor_id] > 1;
            return (
              <li
                key={a.id}
                className={`p-3 rounded-lg border text-sm ${
                  sameProviderConflict ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"
                } ${isSelected ? "ring-2 ring-brand-electric ring-offset-1" : ""}`}
                data-testid={`occupancy-row-${a.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-slate-600">
                    {minutesToHHMM(s)}–{minutesToHHMM(e)}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    {isSelected && (
                      <span className="text-[10px] font-bold text-brand-electric uppercase tracking-wide">Seleccionada</span>
                    )}
                    {sameProviderConflict && (
                      <span className="text-[10px] font-bold text-red-600 uppercase tracking-wide">Mismo prestador</span>
                    )}
                  </div>
                </div>
                <div className="font-medium text-slate-900 mt-1 truncate">{a.patient_name}</div>
                {!isSelected && <Comprobante cita={a} />}
                <div className="text-xs text-slate-500 truncate">
                  {a.service_name || "Sin servicio"} · {a.doctor_name || "Sin prestador"}
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-1">
                    <StatusPill status={a.status} />
                    <AnticipoIndicador cita={a} onAbrir={onAnticipo} testid={`occupancy-anticipo-${a.id}`} />
                  </div>
                  {onCobrar && a.status !== "Cancelado" && (
                    <button
                      type="button"
                      onClick={() => onCobrar(a)}
                      className={`text-xs font-medium inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border ${
                        a.cobro_id
                          ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          : "border-slate-300 text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid={`occupancy-cobrar-${a.id}`}
                    >
                      <Receipt className="w-3.5 h-3.5" />
                      {a.cobro_id ? "Ver cobro" : "Cobrar"}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
